import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useThemeMode } from '../../hooks/useThemeMode';

const CHAIN_IMG = {
  ethereum:    '/logos/ethereum.png',
  solana:      '/logos/solana.png',
  bnb:         '/logos/bnb.png',
  polygon:     '/logos/polygon.png',
  base:        '/logos/base.png',
  hyperliquid: '/logos/hyperliquid.png',
};

const TOKEN_IMG = {
  usdc: 'https://s2.coinmarketcap.com/static/img/coins/64x64/3408.png',
  usdt: 'https://s2.coinmarketcap.com/static/img/coins/64x64/825.png',
  eth:  '/logos/ethereum.png',
};

const DEST_POS = {
  ethereum:     { x: 900,  y: 80,  label: 'Ethereum',    img: 'ethereum' },
  polygon:      { x: 990,  y: 180, label: 'Polygon',     img: 'polygon' },
  bnb:          { x: 1020, y: 295, label: 'BNB',         img: 'bnb' },
  hyperliquid:  { x: 970,  y: 400, label: 'Hyperliquid', img: 'hyperliquid' },
  base:         { x: 840,  y: 470, label: 'Base',        img: 'base' },
  solana:       { x: 820,  y: 275, label: 'Solana',      img: 'solana' },
  ethereum_alt: { x: 880,  y: 160, label: 'Ethereum',    img: 'ethereum' },
  base_alt:     { x: 880,  y: 360, label: 'Base',        img: 'base' },
};

const ADDR = {
  evm:    ['0x8Fc2…3aE9', '0x72bD…01Cf', '0xA41e…9b7a', '0x3eFd…552c', '0xB09a…27De', '0xD1c7…4Aa0'],
  solana: ['8HtKmN…qX4s', 'FzQp2R…vT1u', '2bJnLh…sY9m'],
};

const SCENARIOS = [
  {
    title: 'Fan-out payroll',
    token: 'usdc',
    info: <><b>Deposit 100 USDC on Ethereum</b>. Withdraw across 5 chains, each to a fresh address.</>,
    source: { chain: 'ethereum', label: 'Deposit · Ethereum', amount: '100 USDC' },
    dests: [
      { k: 'polygon',     amt: '20 USDC', addr: ADDR.evm[0] },
      { k: 'bnb',         amt: '20 USDC', addr: ADDR.evm[1] },
      { k: 'hyperliquid', amt: '20 USDC', addr: ADDR.evm[2] },
      { k: 'base',        amt: '20 USDC', addr: ADDR.evm[3] },
      { k: 'solana',      amt: '20 USDC', addr: ADDR.solana[0] },
    ],
  },
  {
    title: 'Same-chain anon',
    token: 'eth',
    info: <><b>Deposit 2 ETH on Ethereum</b>. Withdraw 1 ETH on Ethereum and 1 ETH on Base, to unrelated addresses.</>,
    source: { chain: 'ethereum', label: 'Deposit · Ethereum', amount: '2 ETH' },
    dests: [
      { k: 'ethereum_alt', amt: '1 ETH', addr: ADDR.evm[4] },
      { k: 'base_alt',     amt: '1 ETH', addr: ADDR.evm[5] },
    ],
  },
  {
    title: 'Cross-chain bridge',
    token: 'usdt',
    info: <><b>Deposit 1,000 USDT on Solana</b>. Withdraw 500 USDT on Solana plus 100 USDT each on Ethereum, Polygon, Hyperliquid, BNB, and Base.</>,
    source: { chain: 'solana', label: 'Deposit · Solana', amount: '1,000 USDT' },
    dests: [
      { k: 'solana',      amt: '500 USDT', addr: ADDR.solana[1] },
      { k: 'ethereum',    amt: '100 USDT', addr: ADDR.evm[0] },
      { k: 'polygon',     amt: '100 USDT', addr: ADDR.evm[1] },
      { k: 'hyperliquid', amt: '100 USDT', addr: ADDR.evm[2] },
      { k: 'bnb',         amt: '100 USDT', addr: ADDR.evm[3] },
      { k: 'base',        amt: '100 USDT', addr: ADDR.evm[4] },
    ],
  },
];

const SCEN_DURATION = 7000;
const VB_W = 1200;
const VB_H = 520;
const CX = 520;
const CY = 260;
const DN_RADIUS = 70;

function computeEdgePath(pos) {
  const dx = pos.x - CX;
  const dy = pos.y - CY;
  const len = Math.sqrt(dx * dx + dy * dy);
  const sx = CX + (dx / len) * DN_RADIUS;
  const sy = CY + (dy / len) * DN_RADIUS;
  const mx = (sx + pos.x) / 2;
  const my = (sy + pos.y) / 2;
  const px = (-dy / len) * 30;
  const py = (dx / len) * 30;
  return `M ${sx} ${sy} Q ${mx + px} ${my + py} ${pos.x} ${pos.y}`;
}

const LEGEND = [
  { n: '01 · LOCK',   lead: 'Source funds', rest: ' deposited into the InterNull contract' },
  { n: '02 · SHARD',  lead: '',             rest: 'Distributed Nodes split key shares; no single party reconstructs' },
  { n: '03 · CLAIM',  lead: '',             rest: 'Burn one-time keys to withdraw on any supported chain' },
  { n: '04 · UNLINK', lead: '',             rest: 'Source and destination stay independent. Fresh addresses, zero correlation.' },
];

const DistributedFlow = () => {
  const { isDark } = useThemeMode();
  const [currentIdx, setCurrentIdx] = useState(0);
  const barKeyRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentIdx((i) => (i + 1) % SCENARIOS.length);
    }, SCEN_DURATION);
    return () => clearTimeout(timer);
  }, [currentIdx]);

  const scenario = SCENARIOS[currentIdx];
  const tokenImg = TOKEN_IMG[scenario.token];
  const inletD = `M 198 260 Q 330 260 450 260`;
  const edges = useMemo(() => scenario.dests.map((d) => ({ d, path: computeEdgePath(DEST_POS[d.k]) })), [scenario]);

  const ACCENT = isDark ? '#ffffff' : '#171717';
  const ACCENT_GLOW = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(23,23,23,0.35)';
  const DOT_DIM = isDark ? '#525252' : '#a3a3a3';
  const RING_STROKE = isDark ? '#262626' : '#e5e5e5';
  const cardBg = isDark ? '#0a0a0a' : '#fafafa';
  const cardBord = isDark ? '#171717' : '#e5e5e5';
  const textHi = isDark ? '#ffffff' : '#171717';
  const textMid = isDark ? '#a3a3a3' : '#525252';
  const textLow = isDark ? '#525252' : '#a3a3a3';
  const stepBg = isDark ? '#0f0f0f' : '#f5f5f5';
  const activeBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  return (
    <section className="relative py-24 px-6 max-w-7xl mx-auto">
      <style>{`
        @keyframes intnl-dn-spin { to { transform: rotate(360deg); } }
        @keyframes intnl-march { to { stroke-dashoffset: -980; } }
        @keyframes intnl-tp-go {
          0% { offset-distance: 0%; opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        @keyframes intnl-dest-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes intnl-bar-grow { from { width: 0; } to { width: 100%; } }
        .intnl-dn-ring  { transform-origin: center; animation: intnl-dn-spin 20s linear infinite; }
        .intnl-dn-ring2 { transform-origin: center; animation: intnl-dn-spin 14s linear infinite reverse; }
        .intnl-edge {
          fill: none;
          stroke: ${ACCENT};
          stroke-width: 1;
          stroke-dasharray: 180 800;
          opacity: 0.85;
          filter: drop-shadow(0 0 4px ${ACCENT_GLOW});
          animation: intnl-march 3s linear infinite;
        }
        .intnl-particle { animation: intnl-tp-go 3s linear infinite; }
        .intnl-dest-node {
          position: absolute;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 12px;
          border-radius: 6px;
          background: ${cardBg};
          border: 1px solid ${cardBord};
          animation: intnl-dest-in 500ms cubic-bezier(0.16, 1, 0.3, 1) both;
          z-index: 2;
        }
        .intnl-dest-node.acid {
          border-color: ${ACCENT};
          box-shadow: 0 0 12px ${ACCENT_GLOW};
        }
      `}</style>

      <div className="mb-10">
        <div className="font-mono text-xs tracking-[0.22em] uppercase mb-4" style={{ color: textLow }}>
          Protocol Logic · Live Scenarios
        </div>
        <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-4" style={{ color: textHi }}>
          One deposit. <span style={{ color: textMid }}>Unlinkable withdrawals.</span>
        </h2>
        <p className="text-lg leading-relaxed max-w-3xl" style={{ color: textMid }}>
          Distributed nodes generate the one-time keys you use to withdraw your deposit on any chain, to any
          address, on your own schedule.
        </p>
      </div>

      <div
        className="p-8 rounded-lg relative overflow-hidden"
        style={{
          background: cardBg,
          border: `1px solid ${cardBord}`,
          boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.06)',
        }}
      >
        <div className="grid grid-cols-3 gap-3 mb-5">
          {SCENARIOS.map((s, i) => {
            const active = i === currentIdx;
            const done = i < currentIdx;
            return (
              <button
                key={i}
                onClick={() => {
                  barKeyRef.current += 1;
                  setCurrentIdx(i);
                }}
                className="relative flex items-center gap-3 px-4 py-3.5 rounded text-left overflow-hidden transition-all"
                style={{
                  background: active ? activeBg : stepBg,
                  border: `1px solid ${active ? ACCENT : cardBord}`,
                  boxShadow: active ? `0 0 12px ${ACCENT_GLOW}` : 'none',
                  opacity: done ? 0.55 : 1,
                  cursor: 'pointer',
                }}
              >
                <span
                  className="font-mono text-[11px] tracking-[0.15em]"
                  style={{ color: active ? ACCENT : textLow }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-[13px] font-medium" style={{ color: active ? textHi : textMid }}>
                  {s.title}
                </span>
                {active && (
                  <span
                    key={`bar-${currentIdx}-${barKeyRef.current}`}
                    className="absolute left-0 bottom-0 h-[2px]"
                    style={{
                      background: ACCENT,
                      boxShadow: `0 0 8px ${ACCENT_GLOW}`,
                      animation: `intnl-bar-grow ${SCEN_DURATION}ms linear forwards`,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div
          className="font-mono text-xs mb-3.5"
          style={{ color: textMid, minHeight: 18, letterSpacing: '0.05em' }}
        >
          {scenario.info}
        </div>

        <div className="relative w-full" style={{ height: 520 }}>
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ overflow: 'visible' }}
          >
            <defs>
              <radialGradient id="intnl-dn-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={ACCENT} stopOpacity="0.18" />
                <stop offset="60%" stopColor={ACCENT} stopOpacity="0.05" />
                <stop offset="100%" stopColor={cardBg} stopOpacity="0" />
              </radialGradient>
            </defs>

            <path d={inletD} className="intnl-edge" />
            {[0, 1, 2].map((i) => (
              <circle
                key={`inlet-p-${i}`}
                r="5"
                fill={ACCENT}
                filter={`drop-shadow(0 0 7px ${ACCENT_GLOW})`}
                className="intnl-particle"
                style={{
                  offsetPath: `path('${inletD}')`,
                  offsetRotate: '0deg',
                  animationDelay: `${(i * 0.6).toFixed(2)}s`,
                }}
              />
            ))}

            {edges.map(({ d, path }, i) => (
              <React.Fragment key={`edge-${currentIdx}-${i}`}>
                <path d={path} className="intnl-edge" />
                <circle
                  r="4"
                  fill={ACCENT}
                  filter={`drop-shadow(0 0 6px ${ACCENT_GLOW})`}
                  className="intnl-particle"
                  style={{
                    offsetPath: `path('${path}')`,
                    offsetRotate: '0deg',
                    animationDelay: `${(i * 0.3).toFixed(2)}s`,
                  }}
                />
              </React.Fragment>
            ))}

            <g transform={`translate(${CX} ${CY})`}>
              <circle r="70" fill="url(#intnl-dn-grad)" stroke={RING_STROKE} strokeWidth="1" />
              <g className="intnl-dn-ring">
                <circle cx="0"   cy="-82" r="3.5" fill={ACCENT}  filter={`drop-shadow(0 0 4px ${ACCENT_GLOW})`} />
                <circle cx="71"  cy="-41" r="3"   fill={DOT_DIM} />
                <circle cx="71"  cy="41"  r="3"   fill={DOT_DIM} />
                <circle cx="0"   cy="82"  r="3.5" fill={ACCENT}  filter={`drop-shadow(0 0 4px ${ACCENT_GLOW})`} />
                <circle cx="-71" cy="41"  r="3"   fill={DOT_DIM} />
                <circle cx="-71" cy="-41" r="3"   fill={DOT_DIM} />
                <circle cx="0" cy="0" r="82" fill="none" stroke={RING_STROKE} strokeWidth="1" strokeDasharray="2 4" opacity="0.5" />
              </g>
              <g className="intnl-dn-ring2">
                <circle cx="58"  cy="0"   r="2.5" fill={DOT_DIM} />
                <circle cx="-58" cy="0"   r="2.5" fill={ACCENT} filter={`drop-shadow(0 0 4px ${ACCENT_GLOW})`} />
                <circle cx="41"  cy="41"  r="2.5" fill={DOT_DIM} />
                <circle cx="-41" cy="-41" r="2.5" fill={DOT_DIM} />
                <circle cx="0" cy="0" r="58" fill="none" stroke={RING_STROKE} strokeWidth="1" strokeDasharray="2 3" opacity="0.45" />
              </g>
              <text x="0" y="-4" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill={ACCENT}  letterSpacing="0.18em">DISTRIBUTED</text>
              <text x="0" y="10" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill={ACCENT}  letterSpacing="0.18em">NODES</text>
              <text x="0" y="24" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="8" fill={textLow} letterSpacing="0.18em">7 / 7 ONLINE</text>
            </g>
          </svg>

          <div
            key={`src-${currentIdx}`}
            className="absolute"
            style={{
              left: '11.7%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <div style={{ position: 'relative', width: 92, height: 80, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#fff',
                  border: `2px solid ${cardBg}`,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
                }}
              >
                <img src={tokenImg} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 8,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `2px solid ${cardBg}`,
                  background: stepBg,
                }}
              >
                <img src={CHAIN_IMG[scenario.source.chain]} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
              </div>
            </div>
            <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: textLow, marginTop: 14, whiteSpace: 'nowrap' }}>
              {scenario.source.label}
            </div>
            <div className="font-mono" style={{ fontSize: 13, color: ACCENT, fontWeight: 600, marginTop: 4, whiteSpace: 'nowrap' }}>
              {scenario.source.amount}
            </div>
          </div>

          {scenario.dests.map((d, i) => {
            const pos = DEST_POS[d.k];
            if (!pos) return null;
            const leftPct = (pos.x / VB_W) * 100;
            const topPct = (pos.y / VB_H) * 100;
            return (
              <div
                key={`dest-${currentIdx}-${i}`}
                className={`intnl-dest-node${i === 0 ? ' acid' : ''}`}
                style={{
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  animationDelay: `${(i * 0.15).toFixed(2)}s`,
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: stepBg,
                    border: `1px solid ${cardBord}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'visible',
                    flexShrink: 0,
                  }}
                >
                  <img src={CHAIN_IMG[pos.img]} alt="" style={{ width: 50, height: 50, borderRadius: '50%', display: 'block' }} />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: '#fff',
                      border: `2px solid ${cardBg}`,
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    <img src={tokenImg} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: textLow, whiteSpace: 'nowrap' }}>
                    {pos.label}
                  </div>
                  <div className="font-mono" style={{ fontSize: 12, color: ACCENT, fontWeight: 600, whiteSpace: 'nowrap', marginTop: 3 }}>
                    {d.amt}
                  </div>
                  <div className="font-mono" style={{ fontSize: 10, color: textLow, whiteSpace: 'nowrap', marginTop: 3, letterSpacing: '0.02em' }}>
                    {d.addr}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 pt-5"
          style={{ borderTop: `1px solid ${cardBord}` }}
        >
          {LEGEND.map((lg) => (
            <div key={lg.n} className="font-mono text-xs" style={{ color: textMid }}>
              <span className="block mb-1 font-mono" style={{ fontSize: 10, color: textLow, letterSpacing: '0.22em' }}>
                {lg.n}
              </span>
              {lg.lead && <b style={{ color: textHi, fontWeight: 500 }}>{lg.lead}</b>}
              {lg.rest}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DistributedFlow;
